import React, { useState } from "react";
import { X, PaperPlaneTilt, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, endpoints } from "@/lib/api";

/**
 * Lightweight lead-capture modal shared by every tier CTA on /partners.
 * Persists the lead to /api/partners/leads. Also emails support@eviq.pro
 * via the browser's mailto fallback when the user clicks "Prefer email".
 */
export const PartnerInquiryModal = ({ tier, open, onClose }) => {
    const [form, setForm] = useState({
        contact_name: "",
        company: "",
        email: "",
        phone: "",
        message: "",
    });
    const [busy, setBusy] = useState(false);

    if (!open || !tier) return null;

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.contact_name || !form.company || !form.email) {
            toast.error("Name, company, and email are required.");
            return;
        }
        setBusy(true);
        try {
            await api.post(endpoints.sponsorLeadCreate(), {
                tier_id: tier.id,
                tier_name: tier.name,
                ...form,
            });
            toast.success("Thanks — we'll be in touch.", {
                description: "The EVIQ partnerships team has your inquiry.",
            });
            setForm({ contact_name: "", company: "", email: "", phone: "", message: "" });
            onClose();
        } catch {
            toast.error("Could not send — try again or email support@eviq.pro.");
        } finally {
            setBusy(false);
        }
    };

    const label = "font-mono-metric text-[10px] uppercase tracking-[0.22em] text-zinc-500";
    const input =
        "w-full rounded-md border border-[#27272a] bg-black px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:border-[#a3e635] focus:outline-none";

    return (
        <div
            role="dialog"
            aria-modal="true"
            data-testid="partner-inquiry-modal"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            <form
                onSubmit={submit}
                onClick={(e) => e.stopPropagation()}
                className="relative flex w-full max-w-lg flex-col gap-4 rounded-lg border border-[#1f1f22] bg-[#0a0a0a] p-6 sm:p-8"
            >
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    className="absolute right-4 top-4 rounded-full border border-[#27272a] bg-black p-1.5 text-zinc-400 hover:text-white"
                >
                    <X size={14} />
                </button>
                <div>
                    <span className="eviq-chip">Partner inquiry</span>
                    <h2 className="mt-3 font-editorial text-2xl leading-tight text-white">
                        {tier.name}
                    </h2>
                    <p className="mt-1 text-sm text-zinc-500">
                        {tier.price} · annual investment
                    </p>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label className="flex flex-col gap-1.5">
                        <span className={label}>Your name</span>
                        <input
                            data-testid="partner-lead-name"
                            value={form.contact_name}
                            onChange={set("contact_name")}
                            className={input}
                            required
                        />
                    </label>
                    <label className="flex flex-col gap-1.5">
                        <span className={label}>Company</span>
                        <input
                            data-testid="partner-lead-company"
                            value={form.company}
                            onChange={set("company")}
                            className={input}
                            required
                        />
                    </label>
                    <label className="flex flex-col gap-1.5">
                        <span className={label}>Email</span>
                        <input
                            data-testid="partner-lead-email"
                            type="email"
                            value={form.email}
                            onChange={set("email")}
                            className={input}
                            required
                        />
                    </label>
                    <label className="flex flex-col gap-1.5">
                        <span className={label}>Phone (optional)</span>
                        <input
                            data-testid="partner-lead-phone"
                            value={form.phone}
                            onChange={set("phone")}
                            className={input}
                        />
                    </label>
                </div>
                <label className="flex flex-col gap-1.5">
                    <span className={label}>Anything we should know?</span>
                    <textarea
                        data-testid="partner-lead-message"
                        value={form.message}
                        onChange={set("message")}
                        rows={3}
                        className={`${input} resize-none`}
                        placeholder="Your ESG priorities, timing, questions…"
                    />
                </label>

                <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                    <a
                        href={`mailto:support@eviq.pro?subject=EVIQ%20${encodeURIComponent(tier.name)}%20Inquiry`}
                        className="text-xs text-zinc-500 hover:text-zinc-300"
                    >
                        Prefer email? support@eviq.pro
                    </a>
                    <button
                        type="submit"
                        data-testid="partner-lead-submit"
                        disabled={busy}
                        className="inline-flex items-center gap-2 rounded-full bg-[#a3e635] px-5 py-2.5 text-sm font-semibold text-black transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                    >
                        {busy ? <CircleNotch size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} weight="bold" />}
                        Send inquiry
                    </button>
                </div>
            </form>
        </div>
    );
};

export default PartnerInquiryModal;
